/* AQUON Scratch Offer Popup
   Handles: open trigger, repeat cookie, scratch canvas reveal,
   step transitions (1 scratch -> 2 win -> 3 email), form submit.
*/
class ScratchOfferPopup extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.canvas = this.querySelector('.so__canvas');
    this.threshold = parseInt(this.dataset.threshold, 10) || 60;
    this.trigger = this.dataset.trigger || 'delay';
    this.delayMs = (parseInt(this.dataset.delay, 10) || 3) * 1000;
    this.repeat = this.dataset.repeat || 'every_day';
    this.cookieKey = 'aquon_scratch_popup_seen';
    this._designMode = !!(window.Shopify && window.Shopify.designMode);

    this.pendingKey = 'aquon_scratch_pending';

    this.bindStatic();

    if (this._designMode) {
      // Always show in editor
      this.open();
      return;
    }
    // Returning from a native email submit (Shopify redirects back with
    // ?customer_posted=true) — reopen at the thank-you step.
    if (this.checkPostSubmit()) return;
    if (this.shouldSkip()) return;
    this.scheduleOpen();
  }

  bindStatic() {
    this.querySelectorAll('.so__close').forEach((b) =>
      b.addEventListener('click', () => this.close())
    );
    // Legacy hard-coded claim button (kept for back-compat)
    const claim = this.querySelector('.so__claim');
    if (claim) claim.addEventListener('click', () => this.setStep(3));
    // Block-based buttons: data-so-action="advance" | "close"
    this.querySelectorAll('[data-so-action]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const action = btn.dataset.soAction;
        if (action === 'close') return this.close();
        if (action === 'advance') {
          const current = parseInt(this.dataset.step || '1', 10);
          this.setStep(Math.min(3, current + 1));
        }
      });
    });
    const skip = this.querySelector('.so__skip');
    if (skip) skip.addEventListener('click', () => this.setStep(2));
    // Email form submits NATIVELY so Shopify's spam-protection captcha runs and
    // the customer is actually created. We only flag that a submit is in flight
    // so we can reopen the popup at the thank-you after Shopify redirects back.
    const form = this.querySelector('.so__form');
    if (form) form.addEventListener('submit', () => {
      try { localStorage.setItem(this.pendingKey, '1'); } catch (e) {}
      this.markSeen();
    });
    if (this.dialog) {
      this.dialog.addEventListener('cancel', () => this.markSeen());
      this.dialog.addEventListener('close', () => this.markSeen());
    }
    window.addEventListener('resize', () => this.fitToHeight());
  }

  shouldSkip() {
    const last = localStorage.getItem(this.cookieKey);
    if (!last) return false;
    const win = this.repeatWindow();
    if (win === Infinity) return true;
    return Date.now() - parseInt(last, 10) < win;
  }

  repeatWindow() {
    const m = {
      no_repeat: Infinity,
      every_30_mins: 30 * 60e3,
      every_1_hr: 60 * 60e3,
      every_6_hrs: 6 * 60 * 60e3,
      every_12_hrs: 12 * 60 * 60e3,
      every_day: 24 * 60 * 60e3,
      every_3_days: 3 * 24 * 60 * 60e3,
      every_week: 7 * 24 * 60 * 60e3,
      every_2_weeks: 14 * 24 * 60 * 60e3,
      every_month: 30 * 24 * 60 * 60e3,
    };
    return m[this.repeat] ?? 24 * 60 * 60e3;
  }

  scheduleOpen() {
    if (this.trigger === 'immediately') return this.open();
    if (this.trigger === 'scroll_down') {
      const onScroll = () => {
        if (window.scrollY > 120) {
          window.removeEventListener('scroll', onScroll);
          this.open();
        }
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      return;
    }
    setTimeout(() => this.open(), this.delayMs);
  }

  open() {
    if (!this.dialog) return;
    if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
    else this.dialog.setAttribute('open', '');
    // Init canvas after dialog is visible so layout is measurable
    this._initTries = 0;
    requestAnimationFrame(() => { this.initCanvas(); this.fitToHeight(); });
    // Keep the popup fitted when the viewport height changes (rotation/resize).
    if (!this._resizeBound) {
      this._resizeBound = true;
      window.addEventListener('resize', () => {
        if (!this.dialog || !this.dialog.open) return;
        clearTimeout(this._rt);
        this._rt = setTimeout(() => {
          // Also a last chance for the canvas if it never got a measurable box
          // (initCanvas is a no-op once dataset.ready is set).
          this._initTries = 0;
          this.initCanvas();
          this.fitToHeight();
        }, 120);
      });
    }
  }

  // Scale the card down to fit the available height so the popup never scrolls.
  fitToHeight() {
    const modal = this.querySelector('.so__modal');
    const card = this.querySelector('.so__card');
    if (!modal || !card) return;
    card.style.transform = 'none';
    const avail = modal.clientHeight - 20;
    const natural = card.offsetHeight;
    const scale = natural > avail ? Math.max(0.4, avail / natural) : 1;
    card.style.transform = scale < 1 ? 'scale(' + scale + ')' : 'none';
  }

  close() {
    if (!this.dialog) return;
    if (!this.dialog.open) { this.markSeen(); return; }
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) { this.dialog.close(); this.markSeen(); return; }
    // Play the exit animation (modal scale/fade + backdrop fade) before closing.
    this.classList.add('is-closing');
    setTimeout(() => {
      this.classList.remove('is-closing');
      if (this.dialog.open) this.dialog.close();
      this.markSeen();
    }, 300);
  }

  markSeen() {
    if (this._designMode) return;
    try { localStorage.setItem(this.cookieKey, Date.now().toString()); } catch (e) {}
  }

  setStep(n) {
    this.dataset.step = String(n);
    if (n === 3) {
      const email = this.querySelector('input[type="email"]');
      if (email) setTimeout(() => email.focus(), 60);
    }
  }

  initCanvas() {
    const c = this.canvas;
    if (!c || c.dataset.ready === '1') return;
    // Use offset* (layout size), NOT getBoundingClientRect: the popup's entrance
    // scale animation transforms the box, so getBoundingClientRect would measure
    // a smaller size mid-animation and the drawn "SCRATCH HERE" text would blur
    // once the box settles. offsetWidth/Height ignore the transform.
    const w = c.offsetWidth;
    const h = c.offsetHeight;
    if (!w || !h) {
      // The box is not laid out yet. Safari can take a few frames to resolve
      // dvh and aspect-ratio inside a just-opened <dialog>, and a single silent
      // bail here left the scratch card permanently blank on iPhone. Retry for
      // about a second before giving up.
      this._initTries = (this._initTries || 0) + 1;
      if (this._initTries <= 60) requestAnimationFrame(() => this.initCanvas());
      return;
    }
    // Supersample (>= 2x) so the cover art and hint text stay crisp on any DPR.
    const dpr = Math.max(window.devicePixelRatio || 1, 2);
    c.width = Math.round(w * dpr);
    c.height = Math.round(h * dpr);
    const ctx = c.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.ctx = ctx;
    this._w = w;
    this._h = h;

    // A merchant "Scratch card image" is drawn as the cover; otherwise a flat
    // fill in the scratch colour. The pouch shape itself comes from the CSS mask.
    const imgUrl = c.dataset.scratchImage;
    if (imgUrl) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => { this.drawCover(img); this.finishInit(); };
      img.onerror = () => { this.drawCover(null); this.finishInit(); };
      img.src = imgUrl;
    } else {
      this.drawCover(null);
      this.finishInit();
    }
  }

  drawCover(img) {
    const ctx = this.ctx, w = this._w, h = this._h;
    ctx.globalCompositeOperation = 'source-over';
    ctx.globalAlpha = 1;
    ctx.clearRect(0, 0, w, h);
    this._hasImage = !!img;
    if (img) {
      // cover-fit the uploaded art into the card box
      const ir = img.width / img.height, cr = w / h;
      let dw, dh;
      if (ir > cr) { dh = h; dw = h * ir; } else { dw = w; dh = w / ir; }
      ctx.drawImage(img, (w - dw) / 2, (h - dh) / 2, dw, dh);
    } else {
      ctx.fillStyle = this.canvas.dataset.cover || '#1980C9';
      ctx.fillRect(0, 0, w, h);
    }

    // Hint text — bold, stacked (SCRATCH / HERE)
    const hint = this.canvas.dataset.hint || '';
    if (hint) {
      const fontSize = Math.max(14, Math.round(w * 0.13));
      ctx.fillStyle = '#ffffff';
      ctx.font = `700 ${fontSize}px 'Neue Montreal', system-ui, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      if (img) { ctx.shadowColor = 'rgba(0,0,0,.35)'; ctx.shadowBlur = 6; }
      const words = hint.split(/\s+/);
      const lines = words.length === 2 ? words : [hint];
      const lineH = fontSize * 1.2;
      lines.forEach((line, i) => {
        ctx.fillText(line, w / 2, h / 2 + (i - (lines.length - 1) / 2) * lineH);
      });
      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;
    }
  }

  finishInit() {
    const c = this.canvas;
    this.scratching = false;
    this._strokes = 0;
    this._tainted = false;
    c.dataset.ready = '1';

    // Baseline of already-transparent pixels, so images with a transparent
    // surround don't count as "already scratched".
    try {
      const data = this.ctx.getImageData(0, 0, c.width, c.height).data;
      let cleared = 0, total = 0;
      for (let i = 3; i < data.length; i += 32) { total++; if (data[i] === 0) cleared++; }
      this._baseCleared = cleared;
      this._total = total;
    } catch (e) {
      this._tainted = true; // cross-origin image tainted the canvas
      this._baseCleared = 0;
      this._total = 1;
    }

    c.addEventListener('pointerdown', (e) => this.startScratch(e));
    c.addEventListener('pointermove', (e) => this.moveScratch(e));
    c.addEventListener('pointerup', () => this.endScratch());
    c.addEventListener('pointerleave', () => this.endScratch());
    c.addEventListener('pointercancel', () => this.endScratch());
  }

  startScratch(e) {
    this.scratching = true;
    this.canvas.setPointerCapture?.(e.pointerId);
    this.scratchAt(e);
  }

  moveScratch(e) {
    if (!this.scratching) return;
    this.scratchAt(e);
  }

  scratchAt(e) {
    const rect = this.canvas.getBoundingClientRect();
    // Map the pointer into the canvas coordinate space, accounting for any
    // fit-to-height scaling applied to the card (so the scratch lands correctly).
    const x = rect.width ? (e.clientX - rect.left) / rect.width * this._w : 0;
    const y = rect.height ? (e.clientY - rect.top) / rect.height * this._h : 0;
    this._strokes++;
    this.ctx.globalCompositeOperation = 'destination-out';
    this.ctx.beginPath();
    this.ctx.arc(x, y, 24, 0, Math.PI * 2);
    this.ctx.fill();
  }

  endScratch() {
    if (!this.scratching) return;
    this.scratching = false;
    this.checkReveal();
  }

  checkReveal() {
    const c = this.canvas;
    let pct;
    if (this._tainted) {
      // Can't read pixels (cross-origin image) — fall back to scratch effort
      pct = Math.min(100, this._strokes * 1.4);
    } else {
      try {
        const data = this.ctx.getImageData(0, 0, c.width, c.height).data;
        let cleared = 0;
        for (let i = 3; i < data.length; i += 32) { if (data[i] === 0) cleared++; }
        const coverable = Math.max(1, this._total - this._baseCleared);
        pct = ((cleared - this._baseCleared) / coverable) * 100;
      } catch (e) {
        this._tainted = true;
        pct = Math.min(100, this._strokes * 1.4);
      }
    }
    if (pct >= this.threshold) {
      c.style.transition = 'opacity .35s ease';
      c.style.opacity = '0';
      setTimeout(() => this.setStep(2), 350);
    }
  }

  checkPostSubmit() {
    let params;
    try { params = new URLSearchParams(window.location.search); } catch (e) { return false; }
    const posted = params.get('customer_posted') === 'true' || params.get('contact_posted') === 'true';
    let pending = false;
    try { pending = localStorage.getItem(this.pendingKey) === '1'; } catch (e) {}
    if (!posted || !pending) return false;

    try { localStorage.removeItem(this.pendingKey); } catch (e) {}
    // Strip the query flag so a refresh doesn't reopen the thank-you again
    try {
      params.delete('customer_posted');
      params.delete('contact_posted');
      const qs = params.toString();
      history.replaceState(null, '', window.location.pathname + (qs ? '?' + qs : '') + window.location.hash);
    } catch (e) {}

    this.open();
    this.setStep(3);
    const form = this.querySelector('.so__form');
    if (form) form.classList.add('hidden');
    const success = this.querySelector('.so__success');
    if (success) success.classList.remove('hidden');
    this.markSeen();
    return true;
  }
}

if (!customElements.get('scratch-offer-popup')) {
  customElements.define('scratch-offer-popup', ScratchOfferPopup);
}
